import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, CircularProgress,
  Divider, IconButton, Tooltip, Chip,
} from '@mui/material';
import {
  Backup, Download, Delete, Refresh, Storage,
} from '@mui/icons-material';
import axios from 'axios';

function formatBytes(bytes) {
  if (!bytes || bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function toIST(d) {
  if (!d) return '-';
  let s = String(d);
  if (!s.endsWith('Z') && !s.includes('+')) s += 'Z';
  return new Date(s).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: true,
  });
}

export default function SettingsPage({ apiUrl }) {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [message, setMessage] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${apiUrl}/settings/db-backups`, { headers });
      setBackups(res.data.backups || []);
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to load backups' });
    } finally { setLoading(false); }
  }, [apiUrl]);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const createBackup = async () => {
    setCreating(true);
    setMessage(null);
    try {
      const res = await axios.post(`${apiUrl}/settings/db-backup`, {}, { headers });
      setMessage({ type: 'success', text: `Backup created: ${res.data.filename} (${formatBytes(res.data.size_bytes)})` });
      fetchBackups();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Backup failed' });
    } finally { setCreating(false); }
  };

  const downloadBackup = (filename) => {
    const link = document.createElement('a');
    link.href = `${apiUrl}/settings/db-backup/download/${filename}`;
    link.setAttribute('download', filename);
    // Add auth header via fetch
    fetch(`${apiUrl}/settings/db-backup/download/${filename}`, { headers })
      .then(res => res.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        link.href = url;
        link.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => setMessage({ type: 'error', text: 'Download failed' }));
  };

  const deleteBackup = async (filename) => {
    if (!window.confirm(`Delete backup "${filename}"?`)) return;
    try {
      await axios.delete(`${apiUrl}/settings/db-backup/${filename}`, { headers });
      setMessage({ type: 'success', text: `Deleted ${filename}` });
      fetchBackups();
    } catch (err) {
      setMessage({ type: 'error', text: 'Delete failed' });
    }
  };

  return (
    <Box data-testid="settings-page">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Box display="flex" alignItems="center" gap={1}>
          <Storage sx={{ color: '#3A5BA0' }} />
          <Typography variant="h5">Settings</Typography>
        </Box>
      </Box>

      {/* Database Backup Section */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ px: 2, py: 1.5, bgcolor: 'rgba(58,91,160,0.06)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box display="flex" alignItems="center" gap={1}>
            <Backup sx={{ color: '#3A5BA0', fontSize: 20 }} />
            <Typography variant="subtitle1" fontWeight={700}>Database Backups</Typography>
            <Chip label={`${backups.length} backup${backups.length !== 1 ? 's' : ''}`} size="small"
              sx={{ fontWeight: 600, bgcolor: 'rgba(58,91,160,0.1)', color: '#3A5BA0' }} />
          </Box>
          <Box display="flex" gap={1}>
            <Button variant="outlined" size="small" startIcon={<Refresh />}
              onClick={fetchBackups} disabled={loading} data-testid="refresh-backups-btn">
              Refresh
            </Button>
            <Button variant="contained" size="small"
              startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <Backup />}
              onClick={createBackup} disabled={creating}
              data-testid="create-backup-btn">
              {creating ? 'Creating...' : 'Create Backup'}
            </Button>
          </Box>
        </Box>
        <Divider />

        {message && (
          <Alert severity={message.type} sx={{ m: 1 }} onClose={() => setMessage(null)}>
            {message.text}
          </Alert>
        )}

        {loading && backups.length === 0 ? (
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress size={24} />
          </Box>
        ) : backups.length === 0 ? (
          <Alert severity="info" sx={{ m: 1 }}>No backups yet. Click "Create Backup" to generate your first database backup.</Alert>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Filename</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Size</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Created At (IST)</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {backups.map((b, idx) => (
                  <TableRow key={b.filename} data-testid={`backup-row-${idx}`}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>
                        {b.filename}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={formatBytes(b.size_bytes)} size="small"
                        sx={{ fontWeight: 600, bgcolor: 'rgba(76,175,80,0.1)', color: '#4CAF50' }} />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">{toIST(b.created_at)}</Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" gap={0.5} justifyContent="center">
                        <Tooltip title="Download backup">
                          <IconButton size="small" color="primary" onClick={() => downloadBackup(b.filename)}
                            data-testid={`download-backup-${idx}`}>
                            <Download sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Delete backup">
                          <IconButton size="small" color="error" onClick={() => deleteBackup(b.filename)}
                            data-testid={`delete-backup-${idx}`}>
                            <Delete sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
}
